import React, { useContext } from 'react'
import { getColorImage, getConfigurationColorOption } from 'app/React/Helpers/ColorImageHelper'
import { validateSteps } from 'app/React/Helpers/StepHelper'
import { getCurrentPrice, getSecurityDeposit } from "app/React/Helpers/PriceHelper";
import SpecificVariantSetting from "app/React/Components/Form/Settings/SpecificVariantSetting";
import SpecificEngineTransmissionSetting from "app/React/Components/Form/Settings/SpecificEngineTransmissionSetting";
import { ConfiguratorContext } from "app/React/ConfiguratorContainer";
import { accessoryStorageSet } from "app/React/Helpers/AccessoryStorageHelper";

export function useSetVariant() {
  const {
    cartItems,
    colorSettingId,
    currentEngineTransmission,
    currentLeasingPriceRanges,
    currentModelYear,
    currentVariant,
    disabledEquipmentRequirements,
    engineTransmissions,
    isDealerConfigurator,
    modelYears,
    order,
    variants,
    setCheckedEquipmentRequirements,
    setColorImage,
    setCurrentEngineTransmission,
    setCurrentLeasingPriceRanges,
    setCurrentModelYear,
    setCurrentVariant,
    setDisabledEquipmentRequirements,
    setOrder,
    setValidSteps
  } = useContext(ConfiguratorContext)

  return React.useCallback((option, optionType, paymentType, oldConfiguration = null) => {
    let variant = null
    let modelYear = null
    let engineTransmission = null

    if (optionType === 'variant') {
      variant = variants.find(x => x.id === option.variant_id)
      modelYear = modelYears.find(x => x.id === variant.model_year)
      engineTransmission = engineTransmissions
        .filter(x => x.variant_id == variant.id)
        .find(x => x.hide_on !== paymentType && x.hide_on !== 'all')
    } else if (optionType === 'modelYear') {
      variant = currentVariant
      modelYear = modelYears.find(x => x.id === option.id)
      engineTransmission = engineTransmissions
        .filter(x => x.variant_id == variant.id)
        .find(x => x.hide_on !== paymentType && x.hide_on !== 'all')
    } else if (optionType === 'engineTransmission') {
      variant = currentVariant
      modelYear = currentModelYear
      engineTransmission = engineTransmissions.find(x => x.id === option.engine_transmission_id && x.model_year === modelYear.id)
    } else if (optionType === 'paymentType') {
      const paymentTypeKey = paymentType === 'leasing' ? 'min_leasing_price' : 'min_price'
      const paymentTypeVariants = modelYears
        .map(modelYear => modelYear?.[`${paymentType}_variants`])
        .flat()
        .filter(x => x.hide_on !== paymentType && x.hide_on !== 'all')
        .sort((a, b) => (parseInt(a[paymentTypeKey]) < parseInt(b[paymentTypeKey])) ? -1 : 1)

      variant = variants.find(x => x.id === paymentTypeVariants[0].id)
      modelYear = modelYears.find(x => x.id === variant.model_year)
      engineTransmission = engineTransmissions
        .filter(x => x.variant_id == variant.id)
        .find(x => x.hide_on !== paymentType && x.hide_on !== 'all')
    }

    if (modelYear === null || variant === null || engineTransmission === null) {
      return true
    }

    variant.colorImage = getColorImage(variant)

    setCurrentModelYear({ ...modelYear })
    setCurrentVariant({ ...variant })
    setCurrentEngineTransmission({ ...engineTransmission })

    //reset extra equipments when changing variant
    order.configuration.extra_equipments = {}
    setOrder({ ...order })
    setCheckedEquipmentRequirements({})

    if (disabledEquipmentRequirements !== null) {
      setDisabledEquipmentRequirements({
        required: disabledEquipmentRequirements.required,
        excludes: {}
      })
    }

    // Reset accessories when changing variant
    accessoryStorageSet('cart-items', [])

    if (paymentType === 'leasing') {

      const _currentEngineTransmission = modelYear.leasing_engine_transmissions.find(x => x.id === engineTransmission.id)

      let _leasingPriceRanges = engineTransmission.leasingPriceRanges[0]
      let _leasingPriceRange = engineTransmission.leasingPriceRanges[0][0]

      if (_currentEngineTransmission?.lowest_leasing_price_range !== undefined) {
        _leasingPriceRange = _currentEngineTransmission.lowest_leasing_price_range
        _leasingPriceRanges = engineTransmission.leasingPriceRanges.find(leasingPriceRanges => leasingPriceRanges[0].security_deposit === _currentEngineTransmission.lowest_leasing_price_range.security_deposit)
      }

      order.leasing_price_range = _leasingPriceRange
      setCurrentLeasingPriceRanges([ ..._leasingPriceRanges ])
    } else {
      if (order?.leasing_price_range !== null) {
        order.leasing_price_range = null
      }
      if (!Array.isArray(currentLeasingPriceRanges) || currentLeasingPriceRanges.length > 0) {
        setCurrentLeasingPriceRanges([])
      }
    }

    const color_option = getConfigurationColorOption(oldConfiguration ?? (order.configuration ?? {}), colorSettingId)
    const color_id = typeof color_option === 'object' && color_option !== null ? color_option.id : null
    let color_image = getColorImage(variant, color_id)

    if ((oldConfiguration !== null && color_image === null) || color_image === null) {
      const priceKey = paymentType === 'leasing' ? 'price_leasing' : 'price'
      let mappedColors = JSON.parse(JSON.stringify(variant.mappedColors))
      mappedColors.sort((a, b) => (parseInt(a[priceKey]) < parseInt(b[priceKey])) ? -1 : 1)

      color_image = mappedColors[0]
    }

    order.paymentType = paymentType
    order.configuration.model_year = modelYear
    order.configuration.variants = variant
    order.configuration.engine_transmission = engineTransmission
    order.configuration.color = color_image
    order.security_deposit = getSecurityDeposit(paymentType, order, variant, engineTransmission, [])
    order.price = getCurrentPrice(paymentType, order, variant, [])

    setOrder({ ...order })

    // Color Image Shctuff
    setColorImage(color_image)
    setValidSteps(validateSteps(variant.steps ?? {}, { ...order }, isDealerConfigurator, variant))

    return order
  })
}

export default function VariantSetting() {
  const {
    currentVariant,
    isDealerConfigurator,
    order,
    setValidSteps
  } = useContext(ConfiguratorContext)

  const setVariant = useSetVariant()

  const onChange = React.useCallback(
    function (setting_id, option) {
      setValidSteps(validateSteps(currentVariant.steps ?? {}, { ...order }, isDealerConfigurator, currentVariant))

      return setVariant(option, 'variant', order.paymentType)
    },
    [setVariant]
  )

  const onEngineTransmissionChange = React.useCallback(
    function (setting_id, option) {
      return setVariant(option, 'engineTransmission', order.paymentType)
    },
    [setVariant]
  )

  return (
    <>
      <SpecificVariantSetting onChange={onChange}/>

      <SpecificEngineTransmissionSetting onChange={onEngineTransmissionChange}/>
    </>
  )
}
